import { TelemetryLogger, logger } from "@/logger";

/**
 * Report the failures nothing else sees.
 *
 * An error boundary only catches throws during render. Everything else — an `async` click handler
 * that rejects, a `FileReader` callback, a listener, a promise nobody awaited — lands on
 * `window.onerror` or `unhandledrejection`, and this app had neither. And `logger.error` writes to
 * loglevel, which is the device console: gone the moment the app closes, invisible on production.
 *
 * That gap is why the white screens took so long to place. There was no difference, from the
 * outside, between "the app crashed" and "the app is fine but the screen is blank" — both were
 * silence. Anything that reaches here now reaches Better Stack instead.
 *
 * Returns a teardown so tests can uninstall it; the app installs it once and never removes it.
 * `target` exists for those tests: dispatching a real `error` event at `window` makes jsdom report
 * it as genuinely uncaught, which fails the run for the wrong reason.
 */
export function installGlobalErrorReporting(target: EventTarget = window): () => void {
  const onError = (event: ErrorEvent) => {
    logger.error("[GlobalError] Uncaught error", event.error ?? event.message);
    TelemetryLogger.error("Uncaught error", event.error ?? event.message, {
      type: "uncaught_error",
      // `filename`/`lineno` are the only handle on a minified stack when the Error itself is gone,
      // which is what a cross-origin script error leaves behind.
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    logger.error("[GlobalError] Unhandled promise rejection", event.reason);
    TelemetryLogger.error("Unhandled promise rejection", event.reason, {
      type: "unhandled_rejection",
    });
  };

  target.addEventListener("error", onError as EventListener);
  target.addEventListener("unhandledrejection", onRejection as EventListener);

  return () => {
    target.removeEventListener("error", onError as EventListener);
    target.removeEventListener("unhandledrejection", onRejection as EventListener);
  };
}
